import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DEFAULT_CROP,
  PAGE_HEADER_ASPECT,
  getCropFrames,
  normalizeCrop,
} from '../../lib/collectionHeaders';
import './HeaderCropEditor.css';

const MAX_USER_SCALE = 4;

function coverScale(natural, mask) {
  if (!natural.w || !natural.h || !mask.width || !mask.height) return 1;
  return Math.max(mask.width / natural.w, mask.height / natural.h);
}

function clampTransform(next, natural, mask) {
  const minScale = coverScale(natural, mask);
  const scale = Math.min(Math.max(next.scale, minScale), minScale * MAX_USER_SCALE);
  const imgW = natural.w * scale;
  const imgH = natural.h * scale;
  const minX = mask.left + mask.width - imgW;
  const minY = mask.top + mask.height - imgH;
  return {
    x: Math.min(mask.left, Math.max(minX, next.x)),
    y: Math.min(mask.top, Math.max(minY, next.y)),
    scale,
  };
}

function cropToTransform(crop, natural, mask) {
  const { x: fx, y: fy, scale: userScale } = normalizeCrop(crop);
  const minScale = coverScale(natural, mask);
  const scale = minScale * userScale;
  const imgW = natural.w * scale;
  const imgH = natural.h * scale;
  return clampTransform(
    {
      x: mask.left + mask.width / 2 - fx * imgW,
      y: mask.top + mask.height / 2 - fy * imgH,
      scale,
    },
    natural,
    mask
  );
}

function transformToCrop(transform, natural, mask) {
  const imgW = natural.w * transform.scale;
  const imgH = natural.h * transform.scale;
  const minScale = coverScale(natural, mask);
  return normalizeCrop({
    x: imgW > 0 ? (mask.left + mask.width / 2 - transform.x) / imgW : DEFAULT_CROP.x,
    y: imgH > 0 ? (mask.top + mask.height / 2 - transform.y) / imgH : DEFAULT_CROP.y,
    scale: minScale > 0 ? transform.scale / minScale : DEFAULT_CROP.scale,
  });
}

function readMask(stageEl, maskEl) {
  if (!stageEl || !maskEl) return null;
  const stage = stageEl.getBoundingClientRect();
  const mask = maskEl.getBoundingClientRect();
  return {
    left: mask.left - stage.left,
    top: mask.top - stage.top,
    width: mask.width,
    height: mask.height,
  };
}

function HeaderCropEditor({
  imageUrl,
  title,
  headerKey,
  headerCrop,
  homeCrop,
  saving = false,
  onCancel,
  onChoose,
}) {
  const frames = useMemo(() => getCropFrames(headerKey), [headerKey]);
  const [frameId, setFrameId] = useState(frames[0]?.id || 'header');
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [dragging, setDragging] = useState(false);
  const [ready, setReady] = useState(false);

  const stageRef = useRef(null);
  const maskRef = useRef(null);
  const cropsRef = useRef({
    header_crop: normalizeCrop(headerCrop),
    home_crop: normalizeCrop(homeCrop),
  });
  const pointersRef = useRef(new Map());
  const pinchRef = useRef(null);
  const transformRef = useRef(transform);
  const naturalRef = useRef(natural);
  const frameIdRef = useRef(frameId);

  transformRef.current = transform;
  naturalRef.current = natural;
  frameIdRef.current = frameId;

  const activeFrame = frames.find(frame => frame.id === frameId) || frames[0];

  const applyCropToStage = useCallback(
    crop => {
      const mask = readMask(stageRef.current, maskRef.current);
      const size = naturalRef.current;
      if (!mask || !size.w) return;
      setTransform(cropToTransform(crop, size, mask));
      setReady(true);
    },
    []
  );

  const commitFrameCrop = useCallback(() => {
    const frame = frames.find(item => item.id === frameIdRef.current);
    const mask = readMask(stageRef.current, maskRef.current);
    const size = naturalRef.current;
    if (!frame || !mask || !size.w) return;
    cropsRef.current[frame.cropKey] = transformToCrop(transformRef.current, size, mask);
  }, [frames]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    if (!natural.w) return undefined;
    const stage = stageRef.current;
    if (!stage) return undefined;

    const refresh = () => {
      const frame = frames.find(item => item.id === frameIdRef.current);
      if (!frame) return;
      applyCropToStage(cropsRef.current[frame.cropKey]);
    };

    refresh();
    const observer = new ResizeObserver(refresh);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [natural.w, natural.h, frameId, frames, applyCropToStage]);

  const switchFrame = nextId => {
    if (nextId === frameId) return;
    commitFrameCrop();
    setFrameId(nextId);
    const frame = frames.find(item => item.id === nextId);
    if (frame) applyCropToStage(cropsRef.current[frame.cropKey]);
  };

  const zoomAt = (factor, originX, originY) => {
    const mask = readMask(stageRef.current, maskRef.current);
    const size = naturalRef.current;
    if (!mask || !size.w) return;
    setTransform(current => {
      const minScale = coverScale(size, mask);
      const nextScale = Math.min(
        Math.max(current.scale * factor, minScale),
        minScale * MAX_USER_SCALE
      );
      const ratio = nextScale / current.scale;
      return clampTransform(
        {
          x: originX - (originX - current.x) * ratio,
          y: originY - (originY - current.y) * ratio,
          scale: nextScale,
        },
        size,
        mask
      );
    });
  };

  const userScale = (() => {
    const mask = readMask(stageRef.current, maskRef.current);
    if (!mask || !natural.w) return 1;
    return transform.scale / coverScale(natural, mask);
  })();

  const setUserScale = nextUserScale => {
    const mask = readMask(stageRef.current, maskRef.current);
    if (!mask || !natural.w) return;
    const minScale = coverScale(natural, mask);
    const nextScale = minScale * nextUserScale;
    const factor = nextScale / transform.scale;
    zoomAt(factor, mask.left + mask.width / 2, mask.top + mask.height / 2);
  };

  const onPointerDown = event => {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (pointersRef.current.size === 1) setDragging(true);
    if (pointersRef.current.size === 2) {
      const points = [...pointersRef.current.values()];
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      pinchRef.current = { distance: Math.hypot(dx, dy) || 1 };
    }
  };

  const onPointerMove = event => {
    if (!pointersRef.current.has(event.pointerId)) return;
    const previous = pointersRef.current.get(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointersRef.current.size === 2 && pinchRef.current) {
      const points = [...pointersRef.current.values()];
      const dx = points[1].x - points[0].x;
      const dy = points[1].y - points[0].y;
      const distance = Math.hypot(dx, dy) || 1;
      const factor = distance / pinchRef.current.distance;
      pinchRef.current = { distance };
      const stage = stageRef.current.getBoundingClientRect();
      const originX = (points[0].x + points[1].x) / 2 - stage.left;
      const originY = (points[0].y + points[1].y) / 2 - stage.top;
      zoomAt(factor, originX, originY);
      return;
    }

    if (pointersRef.current.size !== 1) return;
    const mask = readMask(stageRef.current, maskRef.current);
    const size = naturalRef.current;
    if (!mask || !size.w) return;
    setTransform(current =>
      clampTransform(
        {
          ...current,
          x: current.x + (event.clientX - previous.x),
          y: current.y + (event.clientY - previous.y),
        },
        size,
        mask
      )
    );
  };

  const endPointer = event => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) setDragging(false);
  };

  const zoomAtRef = useRef(zoomAt);
  zoomAtRef.current = zoomAt;

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return undefined;
    const onWheel = event => {
      event.preventDefault();
      const bounds = stage.getBoundingClientRect();
      const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08;
      zoomAtRef.current(factor, event.clientX - bounds.left, event.clientY - bounds.top);
    };
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => stage.removeEventListener('wheel', onWheel);
  }, []);

  const handleChoose = () => {
    commitFrameCrop();
    onChoose({
      header_crop: cropsRef.current.header_crop,
      home_crop: cropsRef.current.home_crop,
    });
  };

  return createPortal(
    <div className="header-crop-editor" role="dialog" aria-modal="true" aria-labelledby="header-crop-title">
      <div className="header-crop-editor-top">
        <h2 id="header-crop-title" className="header-crop-editor-title">
          Move and Scale
        </h2>
        <p className="header-crop-editor-subtitle">{title}</p>
        {frames.length > 1 ? (
          <div className="header-crop-editor-tabs" role="tablist">
            {frames.map(frame => (
              <button
                key={frame.id}
                type="button"
                role="tab"
                aria-selected={frame.id === frameId}
                className={`header-crop-editor-tab${frame.id === frameId ? ' is-active' : ''}`}
                onClick={() => switchFrame(frame.id)}
              >
                {frame.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div
        ref={stageRef}
        className={`header-crop-editor-stage${dragging ? ' is-dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPointer}
        onPointerCancel={endPointer}
      >
        {imageUrl ? (
          <img
            className="header-crop-editor-image"
            src={imageUrl}
            alt=""
            draggable={false}
            style={{
              width: natural.w || undefined,
              height: natural.h || undefined,
              transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
              visibility: ready ? 'visible' : 'hidden',
            }}
            onLoad={event => {
              const { naturalWidth, naturalHeight } = event.currentTarget;
              setNatural({ w: naturalWidth, h: naturalHeight });
            }}
          />
        ) : null}
        <div
          ref={maskRef}
          className="header-crop-editor-mask"
          style={{
            aspectRatio: String(activeFrame?.aspect || PAGE_HEADER_ASPECT),
            width: `min(90vw, calc(68dvh * ${activeFrame?.aspect || 1}))`,
          }}
          aria-hidden="true"
        />
        <p className="header-crop-editor-hint">
          Drag to move · scroll or pinch to zoom
        </p>
      </div>

      <div className="header-crop-editor-bottom">
        <button type="button" className="header-crop-editor-btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <label className="header-crop-editor-zoom">
          <span>Scale</span>
          <input
            type="range"
            min="1"
            max={MAX_USER_SCALE}
            step="0.01"
            value={userScale}
            disabled={!ready}
            onChange={event => setUserScale(Number(event.target.value))}
          />
        </label>
        <button
          type="button"
          className="header-crop-editor-btn header-crop-editor-btn--choose"
          onClick={handleChoose}
          disabled={!ready || saving}
        >
          {saving ? 'Saving…' : 'Choose'}
        </button>
      </div>
    </div>,
    document.body
  );
}

export default HeaderCropEditor;
